// ============================================================
// ByOurConsent.com -- Admin Dashboard
// Tabs: Overview | Orders | Articles | Products | Users | Settings
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

// Derives the backend origin from the same env var api.js uses, so
// production, dev, and any future domain/port all stay in sync from
// one place. Falls back to the documented dev port if unset.
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5002/api').replace(/\/api\/?$/, '');

// ============================================================
// Helpers
// ============================================================
const fmt$ = (cents) => `$${(parseInt(cents || 0) / 100).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const statusColor = (status) => ({
    paid:        '#3b6d11', processing: '#1e2a3a',
    shipped:     '#1e5a7a', delivered:  '#3b6d11',
    cancelled:   '#8a8070', refunded:   '#8b6a3a',
    pending:     '#8b6a3a', draft:      '#8a8070',
    published:   '#3b6d11', archived:   '#8a8070',
    active:      '#3b6d11', inactive:   '#8a8070',
    suspended:   '#8b1a1a', pending_review: '#8b6a3a'
}[status] || '#4a5060');

const Badge = ({ status }) => (
    <span style={{
        background:    statusColor(status),
        color:         '#f5f0e8',
        fontSize:      '10px',
        fontFamily:    'sans-serif',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding:       '2px 8px',
        borderRadius:  '2px',
        whiteSpace:    'nowrap'
    }}>
        {status?.replace(/_/g, ' ')}
    </span>
);

const StatCard = ({ label, value, sub, color }) => (
    <div style={{
        background:   '#f5f0e8',
        border:       '0.5px solid #c8beaa',
        borderRadius: '4px',
        padding:      '1rem'
    }}>
        <div style={{
            fontSize:   '11px', fontFamily: 'sans-serif',
            color:      '#8a8070', letterSpacing: '0.1em',
            textTransform: 'uppercase', marginBottom: '6px'
        }}>
            {label}
        </div>
        <div style={{
            fontSize:   '28px', fontWeight: 600,
            color:      color || '#1e2a3a',
            fontFamily: "'EB Garamond', Georgia, serif",
            lineHeight: 1
        }}>
            {value}
        </div>
        {sub && (
            <div style={{
                fontSize: '11px', fontFamily: 'sans-serif',
                color: '#8a8070', marginTop: '4px'
            }}>
                {sub}
            </div>
        )}
    </div>
);

// ============================================================
// Tab components
// ============================================================

// -- OVERVIEW --
const OverviewTab = () => {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get('/admin/dashboard').then(r => setData(r.data)).catch(() => {});
    }, []);

    if (!data) return <div style={{ padding: '2rem', fontFamily: 'sans-serif', color: '#8a8070' }}>Loading...</div>;

    const { stats, recent_orders, recent_users } = data;

    return (
        <div>
            {/* Stats grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '1.5rem' }}>
                <StatCard label="Total members"     value={stats.total_users.toLocaleString()} />
                <StatCard label="Total orders"      value={stats.total_orders.toLocaleString()} />
                <StatCard label="Total revenue"     value={fmt$(stats.total_revenue)} color="#3b6d11" />
                <StatCard label="Email subscribers" value={stats.total_subscribers.toLocaleString()} />
            </div>

            {/* P&L summary */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '1.5rem' }}>
                <StatCard label="Gross revenue"  value={fmt$(stats.pnl.gross_revenue)} />
                <StatCard label="Net revenue"    value={fmt$(stats.pnl.net_revenue)} color="#3b6d11" />
                <StatCard label="Units sold"     value={stats.pnl.units_sold.toLocaleString()} />
            </div>

            {/* Article status */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '2rem' }}>
                {[
                    ['Published',      stats.articles.published     || 0, '#3b6d11'],
                    ['Pending review', stats.articles.pending_review || 0, '#8b6a3a'],
                    ['Drafts',         stats.articles.draft          || 0, '#4a5060'],
                    ['Archived',       stats.articles.archived       || 0, '#8a8070']
                ].map(([label, value, color]) => (
                    <StatCard key={label} label={label} value={value} color={color} />
                ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Recent orders */}
                <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e2a3a', marginBottom: '10px', fontFamily: "'EB Garamond', Georgia, serif" }}>
                        Recent orders
                    </div>
                    {recent_orders.map(o => (
                        <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid #c8beaa', gap: '10px' }}>
                            <div>
                                <div style={{ fontSize: '12px', fontFamily: 'sans-serif', fontWeight: 600, color: '#1e2a3a' }}>{o.order_number}</div>
                                <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070' }}>{o.customer_email}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: 600 }}>{fmt$(o.total_cents)}</span>
                                <Badge status={o.status} />
                            </div>
                        </div>
                    ))}
                </div>

                {/* Recent users */}
                <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e2a3a', marginBottom: '10px', fontFamily: "'EB Garamond', Georgia, serif" }}>
                        Recent members
                    </div>
                    {recent_users.map(u => (
                        <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid #c8beaa' }}>
                            <div>
                                <div style={{ fontSize: '12px', fontFamily: 'sans-serif', fontWeight: 600, color: '#1e2a3a' }}>
                                    {u.first_name} {u.last_name}
                                </div>
                                <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070' }}>{u.email}</div>
                            </div>
                            <Badge status={u.role} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// -- ORDERS --
const OrdersTab = () => {
    const [orders,     setOrders]     = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading,    setLoading]    = useState(true);
    const [search,     setSearch]     = useState('');
    const [status,     setStatus]     = useState('all');
    const [page,       setPage]       = useState(1);
    const [editing,    setEditing]    = useState(null);
    const [editForm,   setEditForm]   = useState({});
    const [saving,     setSaving]     = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        const p = new URLSearchParams({ page, limit: 20 });
        if (status !== 'all') p.set('status', status);
        if (search) p.set('search', search);
        api.get(`/admin/orders?${p}`)
            .then(r => { setOrders(r.data.orders); setPagination(r.data.pagination); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [page, status, search]);

    useEffect(() => { load(); }, [load]);

    const openEdit = (order) => {
        setEditing(order);
        setEditForm({
            status:          order.status || '',
            tracking_number: order.tracking_number || '',
            tracking_url:    order.tracking_url || '',
            carrier:         order.carrier || '',
            notes:           order.notes || ''
        });
    };

    const saveEdit = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/orders/${editing.id}`, editForm);
            toast.success('Order updated');
            setEditing(null);
            load();
        } catch { toast.error('Failed to update order'); }
        finally { setSaving(false); }
    };

    const statuses = ['all', 'paid', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'];
    const iStyle = { width: '100%', background: '#f5f0e8', border: '0.5px solid #c8beaa', padding: '8px 10px', fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', borderRadius: '3px', outline: 'none', boxSizing: 'border-box' };
    const lStyle = { fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', letterSpacing: '0.07em', textTransform: 'uppercase', display: 'block', marginBottom: '4px', marginTop: '10px' };

    return (
        <div>
            {/* Edit modal */}
            {editing && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                    <div style={{ background: '#fff', borderRadius: '4px', padding: '1.5rem', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '4px' }}>
                            Update order #{editing.order_number}
                        </div>
                        <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070', marginBottom: '1rem' }}>
                            {editing.customer_name} · {editing.customer_email}
                        </div>

                        <label style={lStyle}>Status</label>
                        <select style={iStyle} value={editForm.status} onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))}>
                            {['paid','processing','shipped','delivered','cancelled','refunded'].map(s => (
                                <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                            ))}
                        </select>

                        <label style={lStyle}>Tracking number</label>
                        <input style={iStyle} type="text" placeholder="e.g. 1Z999AA10123456784"
                            value={editForm.tracking_number}
                            onChange={e => setEditForm(p => ({ ...p, tracking_number: e.target.value }))} />

                        <label style={lStyle}>Tracking URL (optional)</label>
                        <input style={iStyle} type="url" placeholder="https://..."
                            value={editForm.tracking_url}
                            onChange={e => setEditForm(p => ({ ...p, tracking_url: e.target.value }))} />

                        <label style={lStyle}>Carrier (optional)</label>
                        <input style={iStyle} type="text" placeholder="e.g. UPS, FedEx, USPS"
                            value={editForm.carrier}
                            onChange={e => setEditForm(p => ({ ...p, carrier: e.target.value }))} />

                        <label style={lStyle}>Notes (internal)</label>
                        <textarea style={{ ...iStyle, resize: 'vertical' }} rows={2}
                            value={editForm.notes}
                            onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} />

                        <div style={{ display: 'flex', gap: '8px', marginTop: '1.25rem' }}>
                            <button onClick={saveEdit} disabled={saving}
                                style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '8px 20px', fontSize: '12px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                                {saving ? 'Saving...' : 'Save changes'}
                            </button>
                            <button onClick={() => setEditing(null)}
                                style={{ background: 'transparent', border: '0.5px solid #c8beaa', color: '#6a7080', padding: '8px 16px', fontSize: '12px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer' }}>
                                Cancel
                            </button>
                        </div>

                        {editForm.status === 'shipped' && (
                            <div style={{ marginTop: '10px', fontSize: '11px', fontFamily: 'sans-serif', color: '#3b6d11', padding: '6px 10px', background: '#f0f5ee', borderRadius: '3px' }}>
                                ✓ Shipping notification email will be sent to customer automatically
                            </div>
                        )}
                    </div>
                </div>
            )}
            {/* Filters */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                    type="text" value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && load()}
                    placeholder="Search order #, email, name..."
                    style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', padding: '7px 12px', fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', borderRadius: '3px', width: '240px', outline: 'none' }}
                />
                <div style={{ display: 'flex', gap: '6px' }}>
                    {statuses.map(s => (
                        <button key={s} onClick={() => { setStatus(s); setPage(1); }}
                            style={{ background: status === s ? '#1e2a3a' : 'transparent', color: status === s ? '#e8dfc8' : '#4a5060', border: `0.5px solid ${status === s ? '#1e2a3a' : '#c8beaa'}`, padding: '5px 12px', fontSize: '11px', fontFamily: 'sans-serif', borderRadius: '20px', cursor: 'pointer', textTransform: 'capitalize' }}>
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'sans-serif', color: '#8a8070' }}>Loading...</div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: 'sans-serif' }}>
                    <thead>
                        <tr style={{ background: '#ede8dc' }}>
                            {['Order', 'Customer', 'Items', 'Total', 'Status', 'Date', ''].map(h => (
                                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#4a5060', fontWeight: 600, borderBottom: '0.5px solid #c8beaa', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {orders.map((o, i) => (
                            <tr key={o.id} style={{ background: i % 2 === 0 ? '#f5f0e8' : '#ede8dc' }}>
                                <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e2a3a' }}>{o.order_number}</td>
                                <td style={{ padding: '8px 12px', color: '#4a5060' }}>
                                    <div>{o.customer_name || '—'}</div>
                                    <div style={{ fontSize: '11px', color: '#8a8070' }}>{o.customer_email}</div>
                                </td>
                                <td style={{ padding: '8px 12px', color: '#4a5060', textAlign: 'center' }}>{o.item_count}</td>
                                <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e2a3a' }}>{fmt$(o.total_cents)}</td>
                                <td style={{ padding: '8px 12px' }}><Badge status={o.status} /></td>
                                <td style={{ padding: '8px 12px', color: '#8a8070', whiteSpace: 'nowrap' }}>{fmtDate(o.created_at)}</td>
                                <td style={{ padding: '8px 12px' }}>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        {o.tracking_number && o.tracking_url && (
                                            <a href={o.tracking_url} target="_blank" rel="noopener noreferrer"
                                                style={{ fontSize: '11px', color: '#8b1a1a', textDecoration: 'none' }}>
                                                {o.carrier ? `${o.carrier} Track →` : 'Track →'}
                                            </a>
                                        )}
                                        {o.tracking_number && !o.tracking_url && (
                                            <span style={{ fontSize: '11px', color: '#6a7080' }}>
                                                {o.carrier && `${o.carrier} · `}#{o.tracking_number}
                                            </span>
                                        )}
                                        <button onClick={() => openEdit(o)}
                                            style={{ background: '#1e2a3a', color: '#e8dfc8', border: 'none', padding: '4px 10px', fontSize: '11px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer' }}>
                                            Update
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '1rem', alignItems: 'center' }}>
                    {page > 1 && <button onClick={() => setPage(p => p - 1)} style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '5px 12px', fontSize: '12px', fontFamily: 'sans-serif', cursor: 'pointer', borderRadius: '3px', color: '#4a5060' }}>← Prev</button>}
                    <span style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070' }}>Page {page} of {pagination.total_pages} · {pagination.total} orders</span>
                    {page < pagination.total_pages && <button onClick={() => setPage(p => p + 1)} style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '5px 12px', fontSize: '12px', fontFamily: 'sans-serif', cursor: 'pointer', borderRadius: '3px', color: '#4a5060' }}>Next →</button>}
                </div>
            )}
        </div>
    );
};

// -- ARTICLES --
const ArticlesTab = () => {
    const navigate = useNavigate();
    const [articles,   setArticles]   = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [status,     setStatus]     = useState('all');
    const [search,     setSearch]     = useState('');
    const [reviewing,  setReviewing]  = useState(null);
    const [editingArticle, setEditingArticle] = useState(null);
    const [articleEditForm, setArticleEditForm] = useState({});
    const [categories, setCategories] = useState([]);
    const [reviewForm, setReviewForm] = useState({});
    const [saving,     setSaving]     = useState(false);
    const [contribRequests, setContribRequests] = useState([]);

    const loadContribRequests = useCallback(() => {
        api.get('/admin/contributor-requests')
            .then(r => setContribRequests(r.data.requests || []))
            .catch(() => {});
    }, []);

    useEffect(() => { loadContribRequests(); }, [loadContribRequests]);

    const reviewContribRequest = async (id, action) => {
        try {
            await api.put(`/admin/contributor-requests/${id}`, { action });
            toast.success(action === 'approve' ? 'Contributor approved!' : 'Request rejected');
            loadContribRequests();
        } catch { toast.error('Failed to update request'); }
    };

    const load = useCallback(() => {
        setLoading(true);
        const p = new URLSearchParams({ limit: 50 });
        if (status !== 'all') p.set('status', status);
        if (search) p.set('search', search);
        api.get(`/admin/articles?${p}`)
            .then(r => setArticles(r.data.articles || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [status, search]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.get('/articles/categories')
            .then(r => setCategories(r.data.categories || []))
            .catch(() => {});
    }, []);

    const openArticleEdit = (article) => {
        setEditingArticle(article);
        setArticleEditForm({
            title:       article.title        || '',
            excerpt:     article.excerpt      || '',
            category_id: article.category_id  || ''
        });
    };

    const saveArticleEdit = async () => {
        setSaving(true);
        try {
            await api.put(`/articles/${editingArticle.id}`, {
                title:       articleEditForm.title,
                excerpt:     articleEditForm.excerpt,
                category_id: articleEditForm.category_id || null
            });
            toast.success('Article updated');
            setEditingArticle(null);
            load();
        } catch { toast.error('Failed to update article'); }
        finally { setSaving(false); }
    };

    const openReview = (article) => {
        setReviewing(article);
        setReviewForm({
            title:       article.title,
            excerpt:     article.excerpt || '',
            category_id: article.category_id || '',
            notes:       '',
            showContent: false
        });
    };

    const handleReviewAction = async (action) => {
        setSaving(true);
        try {
            // First update metadata if changed
            if (reviewForm.title !== reviewing.title ||
                reviewForm.excerpt !== reviewing.excerpt ||
                reviewForm.category_id !== reviewing.category_id) {
                await api.put(`/articles/${reviewing.id}`, {
                    title:       reviewForm.title,
                    excerpt:     reviewForm.excerpt,
                    category_id: reviewForm.category_id || null
                });
            }
            // Then take review action
            await api.put(`/articles/${reviewing.id}/review`, {
                action,
                notes: reviewForm.notes
            });
            toast.success(
                action === 'approve'       ? 'Article published!' :
                action === 'request_edits' ? 'Edits requested — contributor notified' :
                'Article rejected'
            );
            setReviewing(null);
            load();
        } catch { toast.error('Action failed'); }
        finally { setSaving(false); }
    };

    const handlePublish = async (id, currentStatus) => {
        const newStatus = currentStatus === 'published' ? 'draft' : 'published';
        try {
            await api.put(`/articles/${id}`, { status: newStatus, publish_now: newStatus === 'published' });
            toast.success(newStatus === 'published' ? 'Article published!' : 'Article unpublished');
            load();
        } catch { toast.error('Failed to update article'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Archive this article?')) return;
        try {
            await api.delete(`/articles/${id}`);
            toast.success('Article archived');
            load();
        } catch { toast.error('Failed to archive article'); }
    };

    const statuses = ['all', 'published', 'pending_review', 'draft', 'archived'];

    const iStyle = { width:'100%', background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'8px 10px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none', boxSizing:'border-box', marginBottom:'12px' };
    const lStyle = { display:'block', fontSize:'11px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' };

    return (
        <div>
            {/* Contributor requests */}
            {contribRequests.filter(r => r.status === 'pending').length > 0 && (
                <div style={{ marginBottom: '1.5rem', background: '#f5f0e8', border: '0.5px solid #8b6a3a', borderLeft: '3px solid #8b6a3a', borderRadius: '4px', padding: '1rem 1.25rem' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '10px' }}>
                        ✍️ Contributor requests ({contribRequests.filter(r => r.status === 'pending').length} pending)
                    </div>
                    {contribRequests.filter(r => r.status === 'pending').map(r => (
                        <div key={r.id} style={{ padding: '10px 0', borderBottom: '0.5px solid #c8beaa' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                                <div>
                                    <div style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: 600 }}>
                                        {r.display_name || `${r.first_name} ${r.last_name}`}
                                        <span style={{ fontWeight: 400, color: '#8a8070', marginLeft: '8px', fontSize: '12px' }}>{r.email}</span>
                                    </div>
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                        Member since {new Date(r.member_since).toLocaleDateString()} · Applied {new Date(r.created_at).toLocaleDateString()}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <button onClick={() => reviewContribRequest(r.id, 'approve')}
                                        style={{ background: '#3b6d11', color: '#f5f0e8', border: 'none', padding: '5px 14px', fontSize: '11px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer' }}>
                                        Approve
                                    </button>
                                    <button onClick={() => reviewContribRequest(r.id, 'reject')}
                                        style={{ background: 'transparent', border: '0.5px solid #c8beaa', color: '#8a8070', padding: '5px 14px', fontSize: '11px', fontFamily: 'sans-serif', borderRadius: '3px', cursor: 'pointer' }}>
                                        Reject
                                    </button>
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px', fontFamily: 'sans-serif' }}>
                                {r.why_contribute && (
                                    <div style={{ gridColumn: '1/-1', background: '#ede8dc', padding: '8px 10px', borderRadius: '3px' }}>
                                        <div style={{ fontSize: '10px', color: '#8a8070', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Why they want to contribute</div>
                                        <div style={{ color: '#1e2a3a' }}>{r.why_contribute}</div>
                                    </div>
                                )}
                                {r.topics && (
                                    <div style={{ background: '#ede8dc', padding: '8px 10px', borderRadius: '3px' }}>
                                        <div style={{ fontSize: '10px', color: '#8a8070', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Topics</div>
                                        <div style={{ color: '#1e2a3a' }}>{r.topics}</div>
                                    </div>
                                )}
                                {r.credentials && (
                                    <div style={{ background: '#ede8dc', padding: '8px 10px', borderRadius: '3px' }}>
                                        <div style={{ fontSize: '10px', color: '#8a8070', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Background</div>
                                        <div style={{ color: '#1e2a3a' }}>{r.credentials}</div>
                                    </div>
                                )}
                                {r.writing_samples && (
                                    <div style={{ background: '#ede8dc', padding: '8px 10px', borderRadius: '3px' }}>
                                        <div style={{ fontSize: '10px', color: '#8a8070', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Previous writing</div>
                                        <div style={{ color: '#1e2a3a' }}>{r.writing_samples}</div>
                                    </div>
                                )}
                                {(r.website || r.social) && (
                                    <div style={{ background: '#ede8dc', padding: '8px 10px', borderRadius: '3px' }}>
                                        <div style={{ fontSize: '10px', color: '#8a8070', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Links</div>
                                        {r.website && <div><a href={r.website} target="_blank" rel="noopener noreferrer" style={{ color: '#8b1a1a' }}>{r.website}</a></div>}
                                        {r.social && <div style={{ color: '#1e2a3a' }}>{r.social}</div>}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Article edit modal */}
            {editingArticle && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <div style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px', padding:'1.5rem', width:'500px', maxWidth:'95vw' }}>
                        <div style={{ fontSize:'18px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif", marginBottom:'4px' }}>
                            Edit article details
                        </div>
                        <div style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#8a8070', marginBottom:'1.25rem', paddingBottom:'10px', borderBottom:'0.5px solid #c8beaa' }}>
                            {editingArticle.title}
                        </div>

                        <label style={{ display:'block', fontSize:'11px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' }}>Title</label>
                        <input
                            style={{ width:'100%', background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'8px 10px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none', boxSizing:'border-box', marginBottom:'12px' }}
                            value={articleEditForm.title}
                            onChange={e => setArticleEditForm(p => ({...p, title: e.target.value}))}
                        />

                        <label style={{ display:'block', fontSize:'11px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' }}>Category</label>
                        <select
                            style={{ width:'100%', background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'8px 10px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none', boxSizing:'border-box', marginBottom:'12px' }}
                            value={articleEditForm.category_id || ''}
                            onChange={e => setArticleEditForm(p => ({...p, category_id: e.target.value || null}))}>
                            <option value="">No category</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>

                        <label style={{ display:'block', fontSize:'11px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' }}>Excerpt</label>
                        <textarea
                            style={{ width:'100%', background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'8px 10px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none', boxSizing:'border-box', resize:'vertical' }}
                            rows={3}
                            value={articleEditForm.excerpt}
                            onChange={e => setArticleEditForm(p => ({...p, excerpt: e.target.value}))}
                            placeholder="Brief summary shown in article listings"
                        />

                        <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'1.25rem', paddingTop:'10px', borderTop:'0.5px solid #c8beaa' }}>
                            <button onClick={() => setEditingArticle(null)}
                                style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'8px 16px', fontSize:'12px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'3px' }}>
                                Cancel
                            </button>
                            <button onClick={saveArticleEdit} disabled={saving}
                                style={{ background:'#1e2a3a', color:'#e8dfc8', border:'none', padding:'8px 16px', fontSize:'12px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'3px' }}>
                                {saving ? 'Saving...' : 'Save changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Review modal */}
            {reviewing && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <div style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px', width:'560px', maxWidth:'95vw', maxHeight:'90vh', display:'flex', flexDirection:'column' }}>

                        {/* Scrollable content */}
                        <div style={{ overflowY:'auto', padding:'1.5rem', flex:1 }}>

                        {/* Modal header */}
                        <div style={{ fontSize:'18px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif", marginBottom:'4px' }}>
                            Review article
                        </div>
                        <div style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#8a8070', marginBottom:'1.25rem', paddingBottom:'10px', borderBottom:'0.5px solid #c8beaa' }}>
                            Submitted by {reviewing.author_name || reviewing.author_display || 'contributor'}
                            {reviewing.submitted_at && ` · ${fmtDate(reviewing.submitted_at)}`}
                        </div>

                        {/* Editable metadata */}
                        <label style={lStyle}>Title</label>
                        <input style={iStyle} value={reviewForm.title}
                            onChange={e => setReviewForm(p => ({...p, title: e.target.value}))} />

                        <label style={lStyle}>Excerpt</label>
                        <textarea style={{...iStyle, resize:'vertical'}} rows={2}
                            value={reviewForm.excerpt}
                            onChange={e => setReviewForm(p => ({...p, excerpt: e.target.value}))}
                            placeholder="Brief summary for article listings" />

                        <label style={lStyle}>Category</label>
                        <select style={iStyle} value={reviewForm.category_id}
                            onChange={e => setReviewForm(p => ({...p, category_id: e.target.value}))}>
                            <option value="">No category</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>

                        {/* View full content link */}
                        <div style={{ marginBottom:'1rem' }}>
                            <button
                                onClick={() => setReviewForm(p => ({...p, showContent: !p.showContent}))}
                                style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'5px 12px', fontSize:'12px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'3px' }}>
                                {reviewForm.showContent ? 'Hide article content ▲' : 'Show article content ▼'}
                            </button>
                        </div>

                        {/* Inline article content */}
                        {reviewForm.showContent && (
                            <div style={{ background:'#ede8dc', border:'0.5px solid #c8beaa', borderRadius:'3px', padding:'1rem', marginBottom:'1rem', maxHeight:'300px', overflowY:'auto' }}>
                                <div className="article-body"
                                    dangerouslySetInnerHTML={{ __html: reviewing.body_html }}
                                />
                            </div>
                        )}

                        {/* Notes field */}
                        <label style={lStyle}>Notes to contributor (if requesting edits or rejecting)</label>
                        <textarea style={{...iStyle, resize:'vertical'}} rows={3}
                            value={reviewForm.notes}
                            onChange={e => setReviewForm(p => ({...p, notes: e.target.value}))}
                            placeholder="Explain what changes are needed or why the article was not accepted..." />
                        </div>

                        {/* Sticky action buttons */}
                        <div style={{ padding:'1rem 1.5rem', borderTop:'0.5px solid #c8beaa', background:'#ede8dc', borderRadius:'0 0 4px 4px', display:'flex', gap:'8px', justifyContent:'space-between', flexWrap:'wrap' }}>
                            <button onClick={() => setReviewing(null)}
                                style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'8px 16px', fontSize:'12px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'3px' }}>
                                Cancel
                            </button>
                            <div style={{ display:'flex', gap:'8px' }}>
                                <button onClick={() => handleReviewAction('reject')} disabled={saving}
                                    style={{ background:'transparent', border:'0.5px solid #8b1a1a', padding:'8px 14px', fontSize:'12px', fontFamily:'sans-serif', color:'#8b1a1a', cursor:'pointer', borderRadius:'3px' }}>
                                    Reject
                                </button>
                                <button onClick={() => handleReviewAction('request_edits')} disabled={saving}
                                    style={{ background:'#8b6a3a', color:'#f5f0e8', border:'none', padding:'8px 14px', fontSize:'12px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'3px' }}>
                                    Request edits
                                </button>
                                <button onClick={() => handleReviewAction('approve')} disabled={saving}
                                    style={{ background:'#3b6d11', color:'#f5f0e8', border:'none', padding:'8px 14px', fontSize:'12px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'3px' }}>
                                    {saving ? 'Publishing...' : 'Approve & publish'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filter bar */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem', flexWrap:'wrap', gap:'10px' }}>
                <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center' }}>
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && load()}
                        placeholder="Search articles..."
                        style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'7px 12px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', width:'200px', outline:'none' }} />
                    {statuses.map(s => (
                        <button key={s} onClick={() => setStatus(s)}
                            style={{ background:status===s?'#1e2a3a':'transparent', color:status===s?'#e8dfc8':'#4a5060', border:`0.5px solid ${status===s?'#1e2a3a':'#c8beaa'}`, padding:'5px 12px', fontSize:'11px', fontFamily:'sans-serif', borderRadius:'20px', cursor:'pointer', textTransform:'capitalize' }}>
                            {s.replace('_',' ')}
                        </button>
                    ))}
                </div>
                <Link to="/submit-article"
                    style={{ background:'#8b1a1a', color:'#f5f0e8', border:'none', padding:'8px 18px', fontSize:'12px', fontFamily:'sans-serif', letterSpacing:'0.06em', textTransform:'uppercase', textDecoration:'none', borderRadius:'3px' }}>
                    + New article
                </Link>
            </div>

            {loading ? (
                <div style={{ padding:'2rem', textAlign:'center', fontFamily:'sans-serif', color:'#8a8070' }}>Loading...</div>
            ) : articles.length === 0 ? (
                <div style={{ padding:'2rem', textAlign:'center', fontFamily:'sans-serif', color:'#8a8070' }}>No articles found.</div>
            ) : (
                articles.map((a, i) => (
                    <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px', gap:'12px', background:i%2===0?'#f5f0e8':'#ede8dc', borderBottom:'0.5px solid #c8beaa' }}>
                        <div style={{ flex:1 }}>
                            <div style={{ fontSize:'14px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif", marginBottom:'2px' }}>
                                {a.title}
                            </div>
                            <div style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070' }}>
                                {a.author_name || a.author_display || 'Editorial Desk'}
                                {' · '}{a.view_count} views
                                {' · '}{fmtDate(a.published_at || a.created_at)}
                            </div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', flexShrink:0 }}>
                            <Badge status={a.status} />
                            <Link to={`/articles/${a.slug}`} target="_blank"
                                style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8b6a3a', textDecoration:'none' }}>
                                View
                            </Link>
                            {/* Review button for pending articles */}
                            {a.status === 'pending_review' ? (
                                <button onClick={() => openReview(a)}
                                    style={{ background:'#8b6a3a', color:'#f5f0e8', border:'none', padding:'4px 10px', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'2px' }}>
                                    Review
                                </button>
                            ) : (
                                <>
                                    <button onClick={() => openArticleEdit(a)}
                                        style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'4px 10px', fontSize:'11px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'2px' }}>
                                        Edit details
                                    </button>
                                    <button onClick={() => handlePublish(a.id, a.status)}
                                        style={{ background:a.status==='published'?'transparent':'#3b6d11', border:`0.5px solid ${a.status==='published'?'#c8beaa':'#3b6d11'}`, color:a.status==='published'?'#4a5060':'#f5f0e8', padding:'4px 10px', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'2px' }}>
                                        {a.status === 'published' ? 'Unpublish' : 'Publish'}
                                    </button>
                                </>
                            )}
                            <button onClick={() => handleDelete(a.id)}
                                style={{ background:'transparent', border:'none', color:'#8a8070', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', textDecoration:'underline' }}>
                                Archive
                            </button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

// -- CATEGORIES --
const CategoriesTab = () => {
    const [categories, setCategories] = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [editingId,  setEditingId]  = useState(null);
    const [editForm,   setEditForm]   = useState({});
    const [adding,     setAdding]     = useState(false);
    const [addForm,    setAddForm]    = useState({ name: '', description: '', color: '#1e2a3a' });
    const [saving,     setSaving]     = useState(false);

    const iStyle = { width:'100%', background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'7px 10px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none', boxSizing:'border-box' };
    const lStyle = { display:'block', fontSize:'11px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.07em', textTransform:'uppercase', marginBottom:'4px', marginTop:'10px' };

    const load = () => {
        setLoading(true);
        api.get('/admin/categories')
            .then(r => setCategories(r.data.categories || []))
            .catch(() => toast.error('Failed to load categories'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const startEdit = (c) => {
        setEditingId(c.id);
        setEditForm({ name: c.name, description: c.description || '', color: c.color, sort_order: c.sort_order });
    };

    const saveEdit = async (id) => {
        setSaving(true);
        try {
            await api.put(`/admin/categories/${id}`, editForm);
            toast.success('Category updated');
            setEditingId(null);
            load();
        } catch { toast.error('Failed to update category'); }
        finally { setSaving(false); }
    };

    const toggleActive = async (c) => {
        try {
            await api.put(`/admin/categories/${c.id}`, { is_active: !c.is_active });
            toast.success(c.is_active ? 'Category deactivated' : 'Category activated');
            load();
        } catch { toast.error('Failed to update category'); }
    };

    const saveAdd = async () => {
        if (!addForm.name) { toast.error('Name is required'); return; }
        setSaving(true);
        try {
            await api.post('/admin/categories', addForm);
            toast.success('Category created');
            setAdding(false);
            setAddForm({ name: '', description: '', color: '#1e2a3a' });
            load();
        } catch { toast.error('Failed to create category'); }
        finally { setSaving(false); }
    };

    if (loading) return <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'sans-serif', color: '#8a8070' }}>Loading...</div>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                    Controls the category filter buttons on the Articles page. Inactive categories stay assigned to their articles but drop out of the public filter list.
                </div>
                <button onClick={() => setAdding(true)}
                    style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '8px 18px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: '3px', whiteSpace: 'nowrap', marginLeft: '1rem' }}>
                    + Add category
                </button>
            </div>

            {adding && (
                <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1rem', marginBottom: '1rem' }}>
                    <label style={lStyle}>Name</label>
                    <input style={iStyle} value={addForm.name}
                        onChange={e => setAddForm(f => ({...f, name: e.target.value}))}
                        placeholder="e.g. Immigration" />
                    <label style={lStyle}>Description</label>
                    <input style={iStyle} value={addForm.description}
                        onChange={e => setAddForm(f => ({...f, description: e.target.value}))}
                        placeholder="Shown as a subtitle where categories are listed" />
                    <label style={lStyle}>Color</label>
                    <input style={{...iStyle, width: '80px', height: '34px', padding: '2px'}} type="color" value={addForm.color}
                        onChange={e => setAddForm(f => ({...f, color: e.target.value}))} />
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '14px' }}>
                        <button onClick={() => setAdding(false)}
                            style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '7px 16px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '3px' }}>
                            Cancel
                        </button>
                        <button onClick={saveAdd} disabled={saving}
                            style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '7px 16px', fontSize: '12px', fontFamily: 'sans-serif', cursor: 'pointer', borderRadius: '3px', opacity: saving ? 0.7 : 1 }}>
                            {saving ? 'Creating...' : 'Create category'}
                        </button>
                    </div>
                </div>
            )}

            {categories.map(c => (
                <div key={c.id} style={{ background: '#faf7f0', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1rem', marginBottom: '0.75rem', opacity: c.is_active ? 1 : 0.55 }}>
                    {editingId === c.id ? (
                        <div>
                            <label style={lStyle}>Name</label>
                            <input style={iStyle} value={editForm.name}
                                onChange={e => setEditForm(f => ({...f, name: e.target.value}))} />
                            <label style={lStyle}>Description</label>
                            <input style={iStyle} value={editForm.description}
                                onChange={e => setEditForm(f => ({...f, description: e.target.value}))} />
                            <label style={lStyle}>Color</label>
                            <input style={{...iStyle, width: '80px', height: '34px', padding: '2px'}} type="color" value={editForm.color}
                                onChange={e => setEditForm(f => ({...f, color: e.target.value}))} />
                            <label style={lStyle}>Sort order</label>
                            <input style={{...iStyle, width: '100px'}} type="number" value={editForm.sort_order}
                                onChange={e => setEditForm(f => ({...f, sort_order: e.target.value}))} />
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '14px' }}>
                                <button onClick={() => setEditingId(null)}
                                    style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '7px 16px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '3px' }}>
                                    Cancel
                                </button>
                                <button onClick={() => saveEdit(c.id)} disabled={saving}
                                    style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '7px 16px', fontSize: '12px', fontFamily: 'sans-serif', cursor: 'pointer', borderRadius: '3px', opacity: saving ? 0.7 : 1 }}>
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: c.color, display: 'inline-block' }} />
                                <div>
                                    <div style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#1e2a3a', fontWeight: 600 }}>
                                        {c.name} {!c.is_active && <span style={{ fontSize: '10px', color: '#8b1a1a', textTransform: 'uppercase', marginLeft: '6px' }}>Inactive</span>}
                                    </div>
                                    {c.description && <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070' }}>{c.description}</div>}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => startEdit(c)}
                                    style={{ background: 'transparent', border: '0.5px solid #c8beaa', color: '#1e2a3a', padding: '5px 12px', fontSize: '11px', fontFamily: 'sans-serif', cursor: 'pointer', borderRadius: '2px' }}>
                                    Edit
                                </button>
                                <button onClick={() => toggleActive(c)}
                                    style={{ background: c.is_active ? 'transparent' : '#3b6d11', border: `0.5px solid ${c.is_active ? '#c8beaa' : '#3b6d11'}`, color: c.is_active ? '#4a5060' : '#f5f0e8', padding: '5px 12px', fontSize: '11px', fontFamily: 'sans-serif', cursor: 'pointer', borderRadius: '2px' }}>
                                    {c.is_active ? 'Deactivate' : 'Activate'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

// -- PRODUCTS --
const blankAddForm = () => ({
    name: '', description: '', price: '', cost: '',
    product_type: 'physical', category: 'apparel',
    printful_product_id: '', primary_image_url: '',
    status: 'active',
    variants: [{ size: '', color: '', color_hex: '', printful_variant_id: '', price_modifier: '', sku: '' }]
});

const ProductsTab = () => {
    const [products, setProducts] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [editing,  setEditing]  = useState(null);
    const [editForm, setEditForm] = useState({});
    const [saving,   setSaving]   = useState(false);
    const [adding,   setAdding]   = useState(false);
    const [addForm,  setAddForm]  = useState(blankAddForm());
    const [addSaving,setAddSaving]= useState(false);
    const [pfLoading,setPfLoading]= useState(false);

    const load = () => {
        setLoading(true);
        api.get('/admin/products')
            .then(r => setProducts(r.data.products || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const openEdit = (p) => {
        setEditing(p);
        setEditForm({
            name:        p.name        || '',
            description: p.description || '',
            price:       p.price       || '',
            cost:        p.cost        || '',
            status:      p.status      || 'active'
        });
    };

    const saveEdit = async () => {
        setSaving(true);
        try {
            await api.put(`/products/${editing.id}`, editForm);
            toast.success('Product updated');
            setEditing(null);
            load();
        } catch { toast.error('Failed to update product'); }
        finally { setSaving(false); }
    };

    const openAdd = () => {
        setAddForm(blankAddForm());
        setAdding(true);
    };

    const updateVariant = (i, field, value) => {
        setAddForm(f => {
            const variants = [...f.variants];
            variants[i] = { ...variants[i], [field]: value };
            return { ...f, variants };
        });
    };

    const addVariantRow = () => {
        setAddForm(f => ({
            ...f,
            variants: [...f.variants, { size: '', color: '', color_hex: '', printful_variant_id: '', price_modifier: '', sku: '' }]
        }));
    };

    const removeVariantRow = (i) => {
        setAddForm(f => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));
    };

    const pullFromPrintful = async () => {
        if (!addForm.printful_product_id) {
            toast.error('Enter the Printful product ID first');
            return;
        }
        setPfLoading(true);
        try {
            const r = await api.get(`/admin/printful/product/${addForm.printful_product_id}`);
            const { name, primary_image_url, variants } = r.data;
            setAddForm(f => ({
                ...f,
                name: f.name || name || f.name,
                primary_image_url: primary_image_url || f.primary_image_url,
                variants: variants.length > 0
                    ? variants.map(v => ({
                        size: v.size,
                        color: v.color,
                        color_hex: '',
                        printful_variant_id: v.printful_variant_id,
                        printful_catalog_variant_id: v.printful_catalog_variant_id,
                        price_modifier: '',
                        sku: v.sku
                      }))
                    : f.variants
            }));
            toast.success(`Pulled ${variants.length} variant${variants.length === 1 ? '' : 's'} from Printful`);
        } catch {
            toast.error('Failed to pull from Printful — check the product ID');
        } finally { setPfLoading(false); }
    };

    // Re-pull and REPLACE variants for an EXISTING product. This is
    // deliberately a separate, explicit action from the regular
    // Edit/Save flow -- editing a product's name/price/etc. never
    // touches variants at all, and this button is the only way
    // variants get replaced, with a confirmation step first since
    // it's destructive.
    const [resyncingId, setResyncingId] = useState(null);
    const resyncVariants = async (product) => {
        if (!product.printful_product_id) {
            toast.error('This product has no Printful product ID on file');
            return;
        }
        if (!window.confirm(`Replace all variants for "${product.name}" with fresh data from Printful? This cannot be undone.`)) {
            return;
        }
        setResyncingId(product.id);
        try {
            const pullRes = await api.get(`/admin/printful/product/${product.printful_product_id}`);
            const variants = pullRes.data.variants || [];
            if (variants.length === 0) {
                toast.error('No variants returned from Printful');
                return;
            }
            await api.put(`/admin/products/${product.id}/resync-variants`, { variants });
            toast.success(`Resynced ${variants.length} variant${variants.length === 1 ? '' : 's'}`);
            load();
        } catch {
            toast.error('Failed to resync variants from Printful');
        } finally {
            setResyncingId(null);
        }
    };

    const saveAdd = async () => {
        if (!addForm.name || !addForm.price) {
            toast.error('Name and price are required');
            return;
        }
        setAddSaving(true);
        try {
            const variants = addForm.variants
                .filter(v => v.size || v.color || v.printful_variant_id)
                .map(v => ({ ...v, price_modifier: v.price_modifier || 0 }));

            await api.post('/products', { ...addForm, variants });
            toast.success('Product created');
            setAdding(false);
            load();
        } catch { toast.error('Failed to create product'); }
        finally { setAddSaving(false); }
    };

    const toggleStatus = async (id, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        try {
            await api.put(`/admin/products/${id}/status`, { status: newStatus });
            toast.success(`Product ${newStatus}`);
            load();
        } catch { toast.error('Failed to update product'); }
    };

    const toggleFeatured = async (id, featured) => {
        try {
            await api.put(`/admin/products/${id}/status`, { is_featured: !featured });
            toast.success(featured ? 'Removed from featured' : 'Set as featured');
            load();
        } catch { toast.error('Failed to update product'); }
    };

    const iStyle = { width:'100%', background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'8px 10px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none', boxSizing:'border-box', marginBottom:'12px' };
    const lStyle = { display:'block', fontSize:'11px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' };

    return (
        <div>
            {/* Edit modal */}
            {editing && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <div style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px', padding:'1.5rem', width:'480px', maxWidth:'95vw' }}>
                        <div style={{ fontSize:'18px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif", marginBottom:'4px' }}>
                            Edit product
                        </div>
                        <div style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#8a8070', marginBottom:'1.25rem', paddingBottom:'10px', borderBottom:'0.5px solid #c8beaa' }}>
                            {editing.name}
                        </div>

                        <label style={lStyle}>Product name</label>
                        <input style={iStyle} value={editForm.name}
                            onChange={e => setEditForm(p => ({...p, name: e.target.value}))} />

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                                <label style={lStyle}>Price ($)</label>
                                <input style={iStyle} type="number" step="0.01" min="0" value={editForm.price}
                                    onChange={e => setEditForm(p => ({...p, price: e.target.value}))} />
                            </div>
                            <div>
                                <label style={lStyle}>Cost / Printful base ($)</label>
                                <input style={iStyle} type="number" step="0.01" min="0" value={editForm.cost || ''}
                                    placeholder="What Printful charges you"
                                    onChange={e => setEditForm(p => ({...p, cost: e.target.value}))} />
                            </div>
                        </div>

                        <label style={lStyle}>Description</label>
                        <textarea style={{...iStyle, resize:'vertical'}} rows={4} value={editForm.description}
                            onChange={e => setEditForm(p => ({...p, description: e.target.value}))}
                            placeholder="Product description shown on the store page" />

                        <label style={lStyle}>Status</label>
                        <select style={iStyle} value={editForm.status}
                            onChange={e => setEditForm(p => ({...p, status: e.target.value}))}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>

                        <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', paddingTop:'10px', borderTop:'0.5px solid #c8beaa' }}>
                            <button onClick={() => setEditing(null)}
                                style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'8px 16px', fontSize:'12px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'3px' }}>
                                Cancel
                            </button>
                            <button onClick={saveEdit} disabled={saving}
                                style={{ background:'#8b1a1a', color:'#f5f0e8', border:'none', padding:'8px 16px', fontSize:'12px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'3px', opacity: saving ? 0.7 : 1 }}>
                                {saving ? 'Saving...' : 'Save changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add product modal */}
            {adding && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem 0' }}>
                    <div style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px', padding:'1.5rem', width:'560px', maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' }}>
                        <div style={{ fontSize:'18px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif", marginBottom:'4px' }}>
                            Add product
                        </div>
                        <div style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#8a8070', marginBottom:'1.25rem', paddingBottom:'10px', borderBottom:'0.5px solid #c8beaa' }}>
                            New products default to Physical / Apparel for Printful tees
                        </div>

                        <label style={lStyle}>Product name</label>
                        <input style={iStyle} value={addForm.name}
                            onChange={e => setAddForm(f => ({...f, name: e.target.value}))}
                            placeholder="Power of the People Tee" />

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                                <label style={lStyle}>Price ($)</label>
                                <input style={iStyle} type="number" step="0.01" min="0" value={addForm.price}
                                    onChange={e => setAddForm(f => ({...f, price: e.target.value}))} />
                            </div>
                            <div>
                                <label style={lStyle}>Cost / Printful base ($)</label>
                                <input style={iStyle} type="number" step="0.01" min="0" value={addForm.cost}
                                    placeholder="What Printful charges you"
                                    onChange={e => setAddForm(f => ({...f, cost: e.target.value}))} />
                            </div>
                        </div>

                        <label style={lStyle}>Description</label>
                        <textarea style={{...iStyle, resize:'vertical'}} rows={3} value={addForm.description}
                            onChange={e => setAddForm(f => ({...f, description: e.target.value}))}
                            placeholder="Product description shown on the store page" />

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                                <label style={lStyle}>Category</label>
                                <input style={iStyle} value={addForm.category}
                                    onChange={e => setAddForm(f => ({...f, category: e.target.value}))} />
                            </div>
                            <div>
                                <label style={lStyle}>Product type</label>
                                <input style={iStyle} value={addForm.product_type}
                                    onChange={e => setAddForm(f => ({...f, product_type: e.target.value}))} />
                            </div>
                        </div>

                        <label style={lStyle}>Printful product ID</label>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                            <input style={{...iStyle, marginBottom: 0, flex: 1}} value={addForm.printful_product_id}
                                onChange={e => setAddForm(f => ({...f, printful_product_id: e.target.value}))}
                                placeholder="From the product page in Printful" />
                            <button onClick={pullFromPrintful} disabled={pfLoading}
                                style={{ background: '#1e2a3a', color: '#e8dfc8', border: 'none', padding: '0 14px', fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.04em', whiteSpace: 'nowrap', cursor: 'pointer', borderRadius: '3px', opacity: pfLoading ? 0.7 : 1 }}>
                                {pfLoading ? 'Pulling...' : 'Pull from Printful'}
                            </button>
                        </div>

                        <label style={lStyle}>Primary image URL</label>
                        <input style={iStyle} value={addForm.primary_image_url}
                            onChange={e => setAddForm(f => ({...f, primary_image_url: e.target.value}))}
                            placeholder="/uploads/images/products/tee-black-front.png" />

                        <label style={lStyle}>Status</label>
                        <select style={iStyle} value={addForm.status}
                            onChange={e => setAddForm(f => ({...f, status: e.target.value}))}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>

                        {/* Variants */}
                        <div style={{ marginTop:'8px', marginBottom:'8px', paddingTop:'12px', borderTop:'0.5px solid #c8beaa' }}>
                            <label style={lStyle}>Variants (sizes / colors)</label>
                            {addForm.variants.map((v, i) => (
                                <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1.4fr 0.9fr auto', gap:'6px', marginBottom:'6px', alignItems:'center' }}>
                                    <input style={{...iStyle, marginBottom:0}} placeholder="Size (M)" value={v.size}
                                        onChange={e => updateVariant(i, 'size', e.target.value)} />
                                    <input style={{...iStyle, marginBottom:0}} placeholder="Color (Black)" value={v.color}
                                        onChange={e => updateVariant(i, 'color', e.target.value)} />
                                    <input style={{...iStyle, marginBottom:0}} placeholder="Printful variant ID" value={v.printful_variant_id}
                                        onChange={e => updateVariant(i, 'printful_variant_id', e.target.value)} />
                                    <input style={{...iStyle, marginBottom:0}} type="number" step="0.01" placeholder="+$ modifier" value={v.price_modifier}
                                        onChange={e => updateVariant(i, 'price_modifier', e.target.value)} />
                                    <button onClick={() => removeVariantRow(i)} title="Remove variant"
                                        style={{ background:'transparent', border:'0.5px solid #c8beaa', color:'#8b1a1a', width:'30px', height:'34px', cursor:'pointer', borderRadius:'3px', fontSize:'14px' }}>
                                        ×
                                    </button>
                                </div>
                            ))}
                            <button onClick={addVariantRow}
                                style={{ background:'transparent', border:'0.5px dashed #c8beaa', color:'#4a5060', padding:'6px 12px', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'3px', marginTop:'4px' }}>
                                + Add another variant
                            </button>
                        </div>

                        <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', paddingTop:'10px', borderTop:'0.5px solid #c8beaa', marginTop:'12px' }}>
                            <button onClick={() => setAdding(false)}
                                style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'8px 16px', fontSize:'12px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'3px' }}>
                                Cancel
                            </button>
                            <button onClick={saveAdd} disabled={addSaving}
                                style={{ background:'#8b1a1a', color:'#f5f0e8', border:'none', padding:'8px 16px', fontSize:'12px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'3px', opacity: addSaving ? 0.7 : 1 }}>
                                {addSaving ? 'Creating...' : 'Create product'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'sans-serif', color: '#8a8070' }}>Loading...</div>
            ) : (
                <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                    <button onClick={openAdd}
                        style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '8px 20px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: '3px' }}>
                        + Add product
                    </button>
                </div>
                {products.map((p, i) => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px', background: i % 2 === 0 ? '#f5f0e8' : '#ede8dc', borderBottom: '0.5px solid #c8beaa' }}>
                        {/* Image */}
                        <div style={{ width: '52px', height: '52px', background: '#d8d0be', borderRadius: '3px', overflow: 'hidden', flexShrink: 0 }}>
                            {p.primary_image_url && (
                                <img src={p.primary_image_url.startsWith('http') ? p.primary_image_url : `${API_ORIGIN}${p.primary_image_url}`} alt={p.name}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            )}
                        </div>
                        {/* Info */}
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                                {p.name}
                                {p.is_featured && <span style={{ marginLeft: '8px', fontSize: '10px', background: '#8b1a1a', color: '#f5f0e8', padding: '1px 6px', borderRadius: '2px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Featured</span>}
                            </div>
                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginTop: '2px' }}>
                                ${parseFloat(p.price).toFixed(2)} · {p.variant_count} variants · {p.units_sold || 0} sold · {fmt$(p.gross_revenue_cents)} revenue
                            </div>
                        </div>
                        {/* Actions */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                            <Badge status={p.status} />
                            <button onClick={() => openEdit(p)}
                                style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'4px 10px', fontSize:'11px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'2px' }}>
                                Edit
                            </button>
                            <button onClick={() => toggleFeatured(p.id, p.is_featured)}
                                style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '4px 10px', fontSize: '11px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '2px' }}>
                                {p.is_featured ? 'Unfeature' : 'Feature'}
                            </button>
                            {p.printful_product_id && (
                                <button onClick={() => resyncVariants(p)} disabled={resyncingId === p.id}
                                    title="Replace this product's variants with fresh data from Printful"
                                    style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '4px 10px', fontSize: '11px', fontFamily: 'sans-serif', color: '#8b6a3a', cursor: resyncingId === p.id ? 'not-allowed' : 'pointer', borderRadius: '2px' }}>
                                    {resyncingId === p.id ? 'Syncing...' : 'Resync Variants'}
                                </button>
                            )}
                            <button onClick={() => toggleStatus(p.id, p.status)}
                                style={{ background: p.status === 'active' ? 'transparent' : '#3b6d11', border: `0.5px solid ${p.status === 'active' ? '#c8beaa' : '#3b6d11'}`, color: p.status === 'active' ? '#4a5060' : '#f5f0e8', padding: '4px 10px', fontSize: '11px', fontFamily: 'sans-serif', cursor: 'pointer', borderRadius: '2px' }}>
                                {p.status === 'active' ? 'Deactivate' : 'Activate'}
                            </button>
                        </div>
                    </div>
                ))}
                </>
            )}
        </div>
    );
};

// Modal component -- defined OUTSIDE UsersTab to prevent re-mount on state changes
const AdminModal = ({ title, sub, onClose, onSave, saveLabel='Save', saving=false, children }) => (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px', padding:'1.5rem', width:'460px', maxWidth:'90vw', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontSize:'18px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif", marginBottom: sub ? '4px' : '1.25rem', paddingBottom: sub ? '0' : '10px', borderBottom: sub ? 'none' : '0.5px solid #c8beaa' }}>{title}</div>
            {sub && <div style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#8a8070', marginBottom:'1.25rem', paddingBottom:'10px', borderBottom:'0.5px solid #c8beaa' }}>{sub}</div>}
            {children}
            <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'1.25rem', paddingTop:'10px', borderTop:'0.5px solid #c8beaa' }}>
                <button onClick={onClose} style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'8px 16px', fontSize:'12px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'3px' }}>Cancel</button>
                <button onClick={onSave} disabled={saving} style={{ background:'#8b1a1a', color:'#f5f0e8', border:'none', padding:'8px 16px', fontSize:'12px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'3px', opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving...' : saveLabel}</button>
            </div>
        </div>
    </div>
);

// -- USERS --
const UsersTab = ({ isSuperAdmin }) => {
    const [users,     setUsers]     = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [search,    setSearch]    = useState('');
    const [managing,  setManaging]  = useState(null);
    const [userPerms, setUserPerms] = useState([]);
    const [editing,   setEditing]   = useState(null);
    const [adding,    setAdding]    = useState(false);
    const [editForm,  setEditForm]  = useState({});
    const [addForm,   setAddForm]   = useState({ first_name:'', last_name:'', email:'', role:'member', password:'' });
    const [saving,    setSaving]    = useState(false);

    const ALL_PERMISSIONS = ['articles','submit_articles','products','orders','users','settings','email','accounting','chapters'];
    const PERM_DESC = { articles:'Create, edit, publish, archive articles', submit_articles:'Submit articles for editorial review (cannot publish)', products:'Add, edit, activate, deactivate products', orders:'View and update order status', users:'View users, change roles, suspend accounts', settings:'Edit site settings and configuration', email:'Manage subscribers and email campaigns', accounting:'View P&L reports and analytics', chapters:'Review and approve chapter applications' };

    const load = useCallback(() => {
        setLoading(true);
        const p = new URLSearchParams({ limit: 50 });
        if (search) p.set('search', search);
        api.get(`/admin/users?${p}`)
            .then(r => setUsers(r.data.users || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [search]);

    useEffect(() => { load(); }, [load]);

    const toggleSuspend = async (id, currentStatus) => {
        const status = currentStatus === 'suspended' ? 'active' : 'suspended';
        try {
            await api.put(`/admin/users/${id}`, { status });
            toast.success(status === 'suspended' ? 'User suspended' : 'User reactivated');
            load();
        } catch { toast.error('Failed to update user'); }
    };

    const openPermissions = async (user) => {
        setManaging(user);
        try {
            const r = await api.get(`/admin/permissions/${user.id}`);
            setUserPerms(r.data.permissions || []);
        } catch { setUserPerms([]); }
    };

    const savePermissions = async () => {
        try {
            await api.put(`/admin/permissions/${managing.id}`, { permissions: userPerms });
            toast.success('Permissions saved');
            setManaging(null);
            load();
        } catch { toast.error('Failed to save permissions'); }
    };

    const openEdit = (user) => {
        setEditing(user);
        setEditForm({ first_name: user.first_name||'', last_name: user.last_name||'', email: user.email||'', role: user.role||'member', new_password: '' });
    };

    const saveEdit = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/users/${editing.id}`, editForm);
            toast.success('User updated');
            setEditing(null);
            load();
        } catch { toast.error('Failed to update user'); }
        finally { setSaving(false); }
    };

    const saveAdd = async () => {
        if (!addForm.email || !addForm.password) { toast.error('Email and password required'); return; }
        setSaving(true);
        try {
            await api.post('/admin/users', addForm);
            toast.success('User created');
            setAdding(false);
            setAddForm({ first_name:'', last_name:'', email:'', role:'member', password:'' });
            load();
        } catch (err) { toast.error(err.response?.data?.error || 'Failed to create user'); }
        finally { setSaving(false); }
    };

    const handleAddChange = (e) => {
        const { name, value } = e.target;
        setAddForm(p => ({ ...p, [name]: value }));
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditForm(p => ({ ...p, [name]: value }));
    };

    const iStyle = { width:'100%', background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'8px 10px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none', boxSizing:'border-box', marginBottom:'12px' };
    const lStyle = { display:'block', fontSize:'11px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' };

    return (
        <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key==='Enter' && load()} placeholder="Search by name or email..." style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'7px 12px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', width:'260px', outline:'none' }} />
                {isSuperAdmin && <button onClick={() => setAdding(true)} style={{ background:'#8b1a1a', color:'#f5f0e8', border:'none', padding:'8px 18px', fontSize:'12px', fontFamily:'sans-serif', letterSpacing:'0.06em', textTransform:'uppercase', cursor:'pointer', borderRadius:'3px' }}>+ Add user</button>}
            </div>

            {/* Add user modal */}
            {adding && (
                <AdminModal title="Add new user" saving={saving} onClose={() => setAdding(false)} onSave={saveAdd} saveLabel="Create user">
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 12px' }}>
                        <div><label style={lStyle}>First name</label><input style={iStyle} name="first_name" value={addForm.first_name} onChange={handleAddChange} placeholder="First" /></div>
                        <div><label style={lStyle}>Last name</label><input style={iStyle} name="last_name" value={addForm.last_name} onChange={handleAddChange} placeholder="Last" /></div>
                    </div>
                    <label style={lStyle}>Email address</label>
                    <input style={iStyle} type="email" name="email" value={addForm.email} onChange={handleAddChange} placeholder="email@example.com" />
                    <label style={lStyle}>Temporary password</label>
                    <input style={iStyle} type="password" name="password" value={addForm.password} onChange={handleAddChange} placeholder="Temporary password" />
                    <label style={lStyle}>Role</label>
                    <select style={iStyle} name="role" value={addForm.role} onChange={handleAddChange}>
                        {['member','contributor','group_leader','admin','superadmin'].map(r=><option key={r} value={r}>{r}</option>)}
                    </select>
                </AdminModal>
            )}

            {/* Edit user modal */}
            {editing && (
                <AdminModal title={`Edit user`} saving={saving} sub={editing.email} onClose={() => setEditing(null)} onSave={saveEdit}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 12px' }}>
                        <div><label style={lStyle}>First name</label><input style={iStyle} name="first_name" value={editForm.first_name} onChange={handleEditChange} /></div>
                        <div><label style={lStyle}>Last name</label><input style={iStyle} name="last_name" value={editForm.last_name} onChange={handleEditChange} /></div>
                    </div>
                    <label style={lStyle}>Email address</label>
                    <input style={iStyle} type="email" name="email" value={editForm.email} onChange={handleEditChange} />
                    <label style={lStyle}>Role</label>
                    <select style={iStyle} name="role" value={editForm.role} onChange={handleEditChange}>
                        {['member','contributor','group_leader','admin','superadmin'].map(r=><option key={r} value={r}>{r}</option>)}
                    </select>
                    <label style={lStyle}>New password (leave blank to keep current)</label>
                    <input style={iStyle} type="password" name="new_password" value={editForm.new_password||''} onChange={handleEditChange} placeholder="Enter new password to reset" />
                </AdminModal>
            )}

            {/* Permissions modal */}
            {managing && (
                <AdminModal title="Admin permissions" sub={`${managing.first_name} ${managing.last_name} — ${managing.email}`} onClose={() => setManaging(null)} onSave={savePermissions} saveLabel="Save permissions">
                    {ALL_PERMISSIONS.map(perm => (
                        <div key={perm} onClick={() => setUserPerms(prev => prev.includes(perm) ? prev.filter(p=>p!==perm) : [...prev,perm])}
                            style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 0', borderBottom:'0.5px solid #c8beaa', cursor:'pointer' }}>
                            <div style={{ width:'18px', height:'18px', background:userPerms.includes(perm)?'#8b1a1a':'#ede8dc', border:`0.5px solid ${userPerms.includes(perm)?'#8b1a1a':'#c8beaa'}`, borderRadius:'2px', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                {userPerms.includes(perm) && <span style={{ color:'#f5f0e8', fontSize:'12px' }}>✓</span>}
                            </div>
                            <div>
                                <div style={{ fontSize:'13px', fontFamily:'sans-serif', fontWeight:600, color:'#1e2a3a', textTransform:'capitalize' }}>{perm}</div>
                                <div style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070' }}>{PERM_DESC[perm]}</div>
                            </div>
                        </div>
                    ))}
                </AdminModal>
            )}

            {loading ? (
                <div style={{ padding:'2rem', textAlign:'center', fontFamily:'sans-serif', color:'#8a8070' }}>Loading...</div>
            ) : (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px', fontFamily:'sans-serif' }}>
                    <thead>
                        <tr style={{ background:'#ede8dc' }}>
                            {['Name','Email','Role','Permissions','Status','Joined','Actions'].map(h=>(
                                <th key={h} style={{ padding:'8px 12px', textAlign:'left', color:'#4a5060', fontWeight:600, borderBottom:'0.5px solid #c8beaa', whiteSpace:'nowrap' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u,i) => (
                            <tr key={u.id} style={{ background: i%2===0?'#f5f0e8':'#ede8dc' }}>
                                <td style={{ padding:'8px 12px', fontWeight:600, color:'#1e2a3a' }}>{u.first_name} {u.last_name}</td>
                                <td style={{ padding:'8px 12px', color:'#4a5060' }}>{u.email}</td>
                                <td style={{ padding:'8px 12px' }}><Badge status={u.role} /></td>
                                <td style={{ padding:'8px 12px' }}>
                                    {u.role==='superadmin' ? (
                                        <span style={{ fontSize:'11px', color:'#8b1a1a', fontStyle:'italic' }}>All</span>
                                    ) : u.role==='admin' ? (
                                        <button onClick={() => isSuperAdmin && openPermissions(u)} style={{ background:'#1e2a3a', border:'none', padding:'3px 10px', fontSize:'11px', fontFamily:'sans-serif', color:'#e8dfc8', cursor:isSuperAdmin?'pointer':'default', borderRadius:'2px' }}>Manage</button>
                                    ) : <span style={{ fontSize:'11px', color:'#8a8070' }}>—</span>}
                                </td>
                                <td style={{ padding:'8px 12px' }}><Badge status={u.status} /></td>
                                <td style={{ padding:'8px 12px', color:'#8a8070', whiteSpace:'nowrap' }}>{fmtDate(u.created_at)}</td>
                                <td style={{ padding:'8px 12px' }}>
                                    <div style={{ display:'flex', gap:'8px' }}>
                                        <button onClick={() => openEdit(u)} style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'3px 8px', fontSize:'11px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'2px' }}>Edit</button>
                                        <button onClick={() => toggleSuspend(u.id, u.status)} style={{ background:'transparent', border:'none', color:u.status==='suspended'?'#3b6d11':'#8b1a1a', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', textDecoration:'underline' }}>
                                            {u.status==='suspended'?'Reactivate':'Suspend'}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

// -- SETTINGS --
// -- CHAPTERS --
const ChaptersTab = () => {
    const [applications, setApplications] = useState([]);
    const [chapters,     setChapters]     = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [activeView,   setActiveView]   = useState('applications');
    const [denyModal,    setDenyModal]    = useState(null);
    const [denyNotes,    setDenyNotes]    = useState('');
    const [saving,       setSaving]       = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.get('/chapters/admin/applications?status=pending'),
            api.get('/chapters?status=all')
        ])
            .then(([apps, chs]) => {
                setApplications(apps.data.applications || []);
                setChapters(chs.data.chapters || []);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const approve = async (id) => {
        setSaving(true);
        try {
            await api.post(`/chapters/admin/approve/${id}`);
            toast.success('Chapter approved and created!');
            load();
        } catch (err) { toast.error(err.response?.data?.error || 'Failed to approve'); }
        finally { setSaving(false); }
    };

    const deny = async () => {
        setSaving(true);
        try {
            await api.put(`/chapters/admin/deny/${denyModal.id}`, { notes: denyNotes });
            toast.success('Application denied');
            setDenyModal(null);
            setDenyNotes('');
            load();
        } catch { toast.error('Failed to deny application'); }
        finally { setSaving(false); }
    };

    const setChapterStatus = async (c, status) => {
        try {
            await api.put(`/chapters/admin/${c.id}/status`, { status });
            toast.success(status === 'active' ? 'Chapter reactivated' : 'Chapter deactivated and hidden from the public site');
            load();
        } catch (err) { toast.error(err.response?.data?.error || 'Failed to update chapter'); }
    };

    const deleteChapter = async (c) => {
        if (!window.confirm(
            `Permanently delete "${c.name}"?\n\n` +
            `This removes the chapter, its member list, and its forum discussions. It cannot be undone.\n\n` +
            `If you only want to hide it, use Deactivate instead.`
        )) return;
        try {
            await api.delete(`/chapters/admin/${c.id}`);
            toast.success('Chapter deleted');
            load();
        } catch (err) {
            // A 409 explains exactly what is still attached to the chapter
            toast.error(err.response?.data?.error || 'Failed to delete chapter', { duration: 9000 });
        }
    };

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

    return (
        <div>
            {/* Deny modal */}
            {denyModal && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <div style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px', padding:'1.5rem', width:'440px', maxWidth:'95vw' }}>
                        <div style={{ fontSize:'17px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif", marginBottom:'1rem' }}>
                            Deny application
                        </div>
                        <div style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#8a8070', marginBottom:'1rem' }}>
                            "{denyModal.name}" by {denyModal.applicant_first} {denyModal.applicant_last}
                        </div>
                        <label style={{ display:'block', fontSize:'11px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' }}>
                            Notes (sent to applicant)
                        </label>
                        <textarea
                            style={{ width:'100%', background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'8px 10px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none', boxSizing:'border-box', resize:'vertical' }}
                            rows={3} value={denyNotes} onChange={e => setDenyNotes(e.target.value)}
                            placeholder="Optional — reason for denial" />
                        <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'1rem' }}>
                            <button onClick={() => { setDenyModal(null); setDenyNotes(''); }}
                                style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'8px 16px', fontSize:'12px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'3px' }}>
                                Cancel
                            </button>
                            <button onClick={deny} disabled={saving}
                                style={{ background:'#8b1a1a', color:'#f5f0e8', border:'none', padding:'8px 16px', fontSize:'12px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'3px' }}>
                                {saving ? 'Denying...' : 'Deny application'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View toggle */}
            <div style={{ display:'flex', gap:'8px', marginBottom:'1rem' }}>
                {[
                    { key:'applications', label:`Applications${applications.length > 0 ? ` (${applications.length})` : ''}` },
                    { key:'chapters',     label:'Chapters' }
                ].map(v => (
                    <button key={v.key} onClick={() => setActiveView(v.key)}
                        style={{ background: activeView===v.key ? '#1e2a3a' : 'transparent', color: activeView===v.key ? '#e8dfc8' : '#4a5060', border:`0.5px solid ${activeView===v.key ? '#1e2a3a' : '#c8beaa'}`, padding:'6px 16px', fontSize:'12px', fontFamily:'sans-serif', borderRadius:'20px', cursor:'pointer' }}>
                        {v.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div style={{ padding:'2rem', textAlign:'center', fontFamily:'sans-serif', color:'#8a8070' }}>Loading...</div>
            ) : activeView === 'applications' ? (
                applications.length === 0 ? (
                    <div style={{ padding:'2rem', textAlign:'center', fontFamily:'sans-serif', color:'#8a8070', background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px' }}>
                        No pending applications
                    </div>
                ) : (
                    applications.map((a, i) => (
                        <div key={a.id} style={{ background: i%2===0 ? '#f5f0e8' : '#ede8dc', border:'0.5px solid #c8beaa', borderRadius:'3px', padding:'1.25rem', marginBottom:'8px' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'12px', flexWrap:'wrap' }}>
                                <div style={{ flex:1 }}>
                                    <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'4px' }}>
                                        <div style={{ fontSize:'16px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif" }}>
                                            {a.name}
                                        </div>
                                        <span style={{ fontSize:'9px', fontFamily:'sans-serif', letterSpacing:'0.12em', textTransform:'uppercase', background: a.chapter_type==='location'?'#e8f0ff':'#f0e8ff', color: a.chapter_type==='location'?'#2a4a8a':'#4a2a8a', padding:'2px 6px', borderRadius:'20px', fontWeight:600 }}>
                                            {a.chapter_type}
                                        </span>
                                    </div>
                                    <div style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#8a8070', marginBottom:'6px' }}>
                                        {a.applicant_first} {a.applicant_last} · {a.email}
                                        {a.phone && ` · ${a.phone}`}
                                        {' · '} Applied {fmtDate(a.created_at)}
                                    </div>
                                    {a.description && (
                                        <div style={{ fontSize:'13px', fontFamily:'sans-serif', color:'#4a5060', lineHeight:1.5, marginBottom:'4px' }}>
                                            {a.description}
                                        </div>
                                    )}
                                    {a.why_lead && (
                                        <div style={{ fontSize:'12px', fontFamily:'sans-serif', color:'#6a7080', fontStyle:'italic' }}>
                                            "{a.why_lead.slice(0, 200)}{a.why_lead.length > 200 ? '...' : ''}"
                                        </div>
                                    )}
                                </div>
                                <div style={{ display:'flex', gap:'8px', flexShrink:0 }}>
                                    <button onClick={() => setDenyModal(a)}
                                        style={{ background:'transparent', border:'0.5px solid #8b1a1a', color:'#8b1a1a', padding:'6px 14px', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'2px' }}>
                                        Deny
                                    </button>
                                    <button onClick={() => approve(a.id)} disabled={saving}
                                        style={{ background:'#3b6d11', color:'#f5f0e8', border:'none', padding:'6px 14px', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'2px' }}>
                                        Approve & create
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )
            ) : (
                chapters.length === 0 ? (
                    <div style={{ padding:'2rem', textAlign:'center', fontFamily:'sans-serif', color:'#8a8070', background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px' }}>
                        No chapters yet
                    </div>
                ) : (
                    chapters.map((c, i) => (
                        <div key={c.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px', background: i%2===0 ? '#f5f0e8' : '#ede8dc', borderBottom:'0.5px solid #c8beaa' }}>
                            <div style={{ flex:1 }}>
                                <div style={{ fontSize:'15px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif" }}>{c.name}</div>
                                <div style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070' }}>
                                    {c.type} · {c.member_count} members · {c.leader_first} {c.leader_last}
                                </div>
                            </div>
                            <Badge status={c.status} />
                            {c.status === 'active' && (
                                <a href={`/chapters/${c.slug}`} target="_blank" rel="noopener noreferrer"
                                    style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8b6a3a', textDecoration:'none' }}>
                                    View →
                                </a>
                            )}
                            <button onClick={() => setChapterStatus(c, c.status === 'active' ? 'inactive' : 'active')}
                                style={{ background: c.status === 'active' ? 'transparent' : '#3b6d11', border:`0.5px solid ${c.status === 'active' ? '#c8beaa' : '#3b6d11'}`, color: c.status === 'active' ? '#4a5060' : '#f5f0e8', padding:'4px 10px', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'2px' }}>
                                {c.status === 'active' ? 'Deactivate' : 'Activate'}
                            </button>
                            <button onClick={() => deleteChapter(c)}
                                style={{ background:'transparent', border:'0.5px solid #d4a8a0', color:'#8b1a1a', padding:'4px 10px', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'2px' }}>
                                Delete
                            </button>
                        </div>
                    ))
                )
            )}
        </div>
    );
};

// -- ACTIONS --
const ActionsTab = () => {
    const [actions,    setActions]    = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [showForm,   setShowForm]   = useState(false);
    const [editing,    setEditing]    = useState(null);
    const [saving,     setSaving]     = useState(false);
    const emptyForm = { title:'', description:'', what_to_do:'', more_info:'', level:'federal', state:'', category_id:'', action_url:'', action_url_label:'Take action', expires_at:'' };
    const [form, setForm] = useState(emptyForm);

    const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];

    const load = useCallback(() => {
        setLoading(true);
        api.get('/civic?status=all&limit=50')
            .then(r => setActions(r.data.actions || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get('/articles/categories')
            .then(r => setCategories(r.data.categories || []))
            .catch(() => {});
    }, []);

    const openEdit = (a) => {
        setEditing(a);
        setForm({
            title: a.title, description: a.description,
            what_to_do: a.what_to_do, more_info: a.more_info || '',
            level: a.level,
            state: a.state || '', category_id: a.category_id || '',
            action_url: a.action_url || '', action_url_label: a.action_url_label || 'Take action',
            expires_at: a.expires_at ? a.expires_at.slice(0,10) : '',
            status: a.status
        });
        setShowForm(true);
    };

    const save = async () => {
        if (!form.title || !form.description || !form.what_to_do) {
            toast.error('Title, description and what to do are required');
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/civic/${editing.id}`, form);
                toast.success('Action updated');
            } else {
                await api.post('/civic', form);
                toast.success('Action created');
            }
            setShowForm(false);
            setEditing(null);
            setForm(emptyForm);
            load();
        } catch (err) { toast.error(err.response?.data?.error || 'Failed to save'); }
        finally { setSaving(false); }
    };

    const toggleStatus = async (a) => {
        const newStatus = a.status === 'active' ? 'inactive' : 'active';
        try {
            await api.put(`/civic/${a.id}`, { status: newStatus });
            toast.success(newStatus === 'active' ? 'Action activated' : 'Action deactivated');
            load();
        } catch { toast.error('Failed to update status'); }
    };

    const iStyle = { width:'100%', background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'8px 10px', fontSize:'13px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none', boxSizing:'border-box' };
    const lStyle = { display:'block', fontSize:'11px', fontFamily:'sans-serif', color:'#6a7080', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' };

    return (
        <div>
            {/* Form */}
            {showForm && (
                <div style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px', padding:'1.5rem', marginBottom:'1.5rem' }}>
                    <div style={{ fontSize:'17px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif", marginBottom:'1rem', paddingBottom:'8px', borderBottom:'0.5px solid #c8beaa' }}>
                        {editing ? 'Edit action' : 'New action item'}
                    </div>

                    <div style={{ marginBottom:'10px' }}><label style={lStyle}>Title *</label><input style={iStyle} value={form.title} onChange={e => setForm(p=>({...p,title:e.target.value}))} placeholder="Action title" /></div>

                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', marginBottom:'10px' }}>
                        <div>
                            <label style={lStyle}>Level</label>
                            <select style={iStyle} value={form.level} onChange={e => setForm(p=>({...p,level:e.target.value}))}>
                                <option value="federal">Federal</option>
                                <option value="state">State</option>
                            </select>
                        </div>
                        {form.level === 'state' && (
                            <div>
                                <label style={lStyle}>State</label>
                                <select style={iStyle} value={form.state} onChange={e => setForm(p=>({...p,state:e.target.value}))}>
                                    <option value="">Select state</option>
                                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        )}
                        <div>
                            <label style={lStyle}>Category</label>
                            <select style={iStyle} value={form.category_id} onChange={e => setForm(p=>({...p,category_id:e.target.value}))}>
                                <option value="">No category</option>
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        {editing && (
                            <div>
                                <label style={lStyle}>Status</label>
                                <select style={iStyle} value={form.status} onChange={e => setForm(p=>({...p,status:e.target.value}))}>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        )}
                    </div>

                    <div style={{ marginBottom:'10px' }}><label style={lStyle}>What's happening (description) *</label><textarea style={{...iStyle,resize:'vertical'}} rows={4} value={form.description} onChange={e => setForm(p=>({...p,description:e.target.value}))} placeholder="Explain what's happening and why it matters..." /></div>

                    <div style={{ marginBottom:'10px' }}><label style={lStyle}>What citizens can do *</label><textarea style={{...iStyle,resize:'vertical'}} rows={4} value={form.what_to_do} onChange={e => setForm(p=>({...p,what_to_do:e.target.value}))} placeholder="Specific steps: call your senator, sign the petition, attend the meeting..." /></div>

                    <div style={{ marginBottom:'10px' }}>
                        <label style={lStyle}>More information (supports markdown links)</label>
                        <textarea style={{...iStyle,resize:'vertical'}} rows={6} value={form.more_info} onChange={e => setForm(p=>({...p,more_info:e.target.value}))}
                            placeholder={'Add context, resources, links...\n\nUse [link text](https://url.com) for links\nUse **text** for bold\nUse - item for bullet lists'} />
                        <div style={{ fontSize:'10px', fontFamily:'sans-serif', color:'#8a8070', marginTop:'3px' }}>
                            Links: [link text](https://url.com) · Bold: **text** · Bullets: - item
                        </div>
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:'10px', marginBottom:'10px' }}>
                        <div><label style={lStyle}>Action URL</label><input style={iStyle} value={form.action_url} onChange={e => setForm(p=>({...p,action_url:e.target.value}))} placeholder="https://..." /></div>
                        <div><label style={lStyle}>Button label</label><input style={iStyle} value={form.action_url_label} onChange={e => setForm(p=>({...p,action_url_label:e.target.value}))} placeholder="Take action" /></div>
                        <div><label style={lStyle}>Expires</label><input style={iStyle} type="date" value={form.expires_at} onChange={e => setForm(p=>({...p,expires_at:e.target.value}))} /></div>
                    </div>

                    <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', paddingTop:'10px', borderTop:'0.5px solid #c8beaa' }}>
                        <button onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm); }}
                            style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'8px 16px', fontSize:'12px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'3px' }}>
                            Cancel
                        </button>
                        <button onClick={save} disabled={saving}
                            style={{ background:'#8b1a1a', color:'#f5f0e8', border:'none', padding:'8px 16px', fontSize:'12px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'3px', opacity: saving ? 0.7 : 1 }}>
                            {saving ? 'Saving...' : editing ? 'Save changes' : 'Create action'}
                        </button>
                    </div>
                </div>
            )}

            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
                <div style={{ fontSize:'13px', fontFamily:'sans-serif', color:'#8a8070' }}>
                    {actions.length} action items
                </div>
                {!showForm && (
                    <button onClick={() => { setShowForm(true); setEditing(null); setForm(emptyForm); }}
                        style={{ background:'#8b1a1a', color:'#f5f0e8', border:'none', padding:'8px 18px', fontSize:'12px', fontFamily:'sans-serif', letterSpacing:'0.06em', textTransform:'uppercase', cursor:'pointer', borderRadius:'3px' }}>
                        + New action
                    </button>
                )}
            </div>

            {/* List */}
            {loading ? (
                <div style={{ padding:'2rem', textAlign:'center', fontFamily:'sans-serif', color:'#8a8070' }}>Loading...</div>
            ) : actions.length === 0 ? (
                <div style={{ padding:'2rem', textAlign:'center', fontFamily:'sans-serif', color:'#8a8070', background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px' }}>
                    No action items yet. Create your first one above.
                </div>
            ) : (
                actions.map((a, i) => (
                    <div key={a.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px', background: i%2===0 ? '#f5f0e8' : '#ede8dc', borderBottom:'0.5px solid #c8beaa' }}>
                        <div style={{ flex:1 }}>
                            <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'3px' }}>
                                <span style={{ fontSize:'9px', fontFamily:'sans-serif', letterSpacing:'0.1em', textTransform:'uppercase', fontWeight:600, padding:'2px 6px', borderRadius:'20px', background: a.level==='federal'?'#e8ecf0':'#f0e8ff', color: a.level==='federal'?'#2a4a8a':'#6a2a8a' }}>
                                    {a.level==='federal' ? 'Federal' : a.state || 'State'}
                                </span>
                                <span style={{ fontSize:'9px', fontFamily:'sans-serif', letterSpacing:'0.1em', textTransform:'uppercase', fontWeight:600, padding:'2px 6px', borderRadius:'20px', background: a.status==='active'?'#e8f5e0':'#ede8dc', color: a.status==='active'?'#3b6d11':'#8a8070' }}>
                                    {a.status}
                                </span>
                                {a.category_name && <span style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070' }}>{a.category_name}</span>}
                            </div>
                            <div style={{ fontSize:'14px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif" }}>{a.title}</div>
                            <div style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070' }}>{fmtDate(a.created_at)} · {a.view_count} views</div>
                        </div>
                        <div style={{ display:'flex', gap:'8px', flexShrink:0 }}>
                            <button onClick={() => openEdit(a)}
                                style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'4px 10px', fontSize:'11px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'2px' }}>
                                Edit
                            </button>
                            <button onClick={() => toggleStatus(a)}
                                style={{ background: a.status === 'active' ? 'transparent' : '#3b6d11', border: `0.5px solid ${a.status === 'active' ? '#c8beaa' : '#3b6d11'}`, color: a.status === 'active' ? '#4a5060' : '#f5f0e8', padding:'4px 10px', fontSize:'11px', fontFamily:'sans-serif', cursor:'pointer', borderRadius:'2px' }}>
                                {a.status === 'active' ? 'Deactivate' : 'Activate'}
                            </button>
                            <a href={`/civic/${a.slug}`} target="_blank" rel="noopener noreferrer"
                                style={{ background:'transparent', border:'0.5px solid #c8beaa', padding:'4px 10px', fontSize:'11px', fontFamily:'sans-serif', color:'#8b6a3a', cursor:'pointer', borderRadius:'2px', textDecoration:'none' }}>
                                View
                            </a>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

const ForumTab = () => {
    const [threads, setThreads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter,  setFilter]  = useState('all');

    const load = useCallback(() => {
        setLoading(true);
        api.get(`/forum/threads?status=${filter}&limit=100`)
            .then(r => setThreads(r.data.threads || []))
            .catch(() => toast.error('Failed to load forum threads'))
            .finally(() => setLoading(false));
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const setStatus = async (thread, status) => {
        try {
            await api.put(`/forum/threads/${thread.id}`, { status });
            toast.success(status === 'published' ? 'Thread published' : 'Thread archived');
            load();
        } catch { toast.error('Failed to update thread'); }
    };

    const togglePinned = async (thread) => {
        try {
            await api.put(`/forum/threads/${thread.id}`, { is_pinned: !thread.is_pinned });
            load();
        } catch { toast.error('Failed to update thread'); }
    };

    const toggleLocked = async (thread) => {
        try {
            await api.put(`/forum/threads/${thread.id}`, { is_locked: !thread.is_locked });
            load();
        } catch { toast.error('Failed to update thread'); }
    };

    const deleteThread = async (thread) => {
        if (!window.confirm(`Permanently delete "${thread.title}" and all its replies? This cannot be undone.`)) return;
        try {
            await api.delete(`/forum/threads/${thread.id}`);
            toast.success('Thread deleted');
            load();
        } catch { toast.error('Failed to delete thread'); }
    };

    const btnStyle = { background:'transparent', border:'0.5px solid #c8beaa', padding:'4px 10px', fontSize:'11px', fontFamily:'sans-serif', color:'#4a5060', cursor:'pointer', borderRadius:'2px', whiteSpace:'nowrap' };

    return (
        <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
                <div style={{ fontSize:'13px', fontFamily:'sans-serif', color:'#8a8070' }}>
                    {threads.length} thread{threads.length === 1 ? '' : 's'}
                </div>
                <select value={filter} onChange={e => setFilter(e.target.value)}
                    style={{ background:'#f5f0e8', border:'0.5px solid #c8beaa', padding:'6px 10px', fontSize:'12px', fontFamily:'sans-serif', color:'#1e2a3a', borderRadius:'3px', outline:'none' }}>
                    <option value="all">All statuses</option>
                    <option value="published">Published only</option>
                    <option value="archived">Archived only</option>
                </select>
            </div>

            {loading ? (
                <div style={{ padding:'2rem', textAlign:'center', fontFamily:'sans-serif', color:'#8a8070' }}>Loading...</div>
            ) : threads.length === 0 ? (
                <div style={{ padding:'2rem', textAlign:'center', fontFamily:'sans-serif', color:'#8a8070', background:'#f5f0e8', border:'0.5px solid #c8beaa', borderRadius:'4px' }}>
                    No threads found for this filter.
                </div>
            ) : (
                threads.map((t, i) => (
                    <div key={t.id} style={{ padding:'12px', background: i%2===0 ? '#f5f0e8' : '#ede8dc', borderBottom:'0.5px solid #c8beaa' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'12px' }}>
                            <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ display:'flex', gap:'6px', alignItems:'center', marginBottom:'3px', flexWrap:'wrap' }}>
                                    <span style={{ fontSize:'9px', fontFamily:'sans-serif', letterSpacing:'0.1em', textTransform:'uppercase', fontWeight:600, padding:'2px 6px', borderRadius:'20px', background: t.status==='published' ? '#e8f5e0' : '#ede8dc', color: t.status==='published' ? '#3b6d11' : '#8a8070' }}>
                                        {t.status}
                                    </span>
                                    {t.is_pinned && <span style={{ fontSize:'9px', fontFamily:'sans-serif', letterSpacing:'0.1em', textTransform:'uppercase', fontWeight:600, padding:'2px 6px', borderRadius:'20px', background:'#f0e8ff', color:'#6a2a8a' }}>Pinned</span>}
                                    {t.is_locked && <span style={{ fontSize:'9px', fontFamily:'sans-serif', letterSpacing:'0.1em', textTransform:'uppercase', fontWeight:600, padding:'2px 6px', borderRadius:'20px', background:'#fdf0ee', color:'#8b1a1a' }}>Locked</span>}
                                    {t.category_name && <span style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070' }}>{t.category_name}</span>}
                                </div>
                                <div style={{ fontSize:'14px', fontWeight:600, color:'#1e2a3a', fontFamily:"'EB Garamond', Georgia, serif" }}>{t.title}</div>
                                <div style={{ fontSize:'11px', fontFamily:'sans-serif', color:'#8a8070' }}>
                                    {t.author_name} · {fmtDate(t.created_at)} · {t.reply_count || 0} replies · {t.view_count || 0} views
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:'6px', flexShrink:0, flexWrap:'wrap', justifyContent:'flex-end', maxWidth:'260px' }}>
                                <a href={`/forum/${t.slug}`} target="_blank" rel="noopener noreferrer" style={{...btnStyle, color:'#8b6a3a', textDecoration:'none'}}>View</a>
                                <button onClick={() => togglePinned(t)} style={btnStyle}>{t.is_pinned ? 'Unpin' : 'Pin'}</button>
                                <button onClick={() => toggleLocked(t)} style={btnStyle}>{t.is_locked ? 'Unlock' : 'Lock'}</button>
                                {t.status === 'published' ? (
                                    <button onClick={() => setStatus(t, 'archived')} style={btnStyle}>Archive</button>
                                ) : (
                                    <button onClick={() => setStatus(t, 'published')}
                                        style={{...btnStyle, background:'#3b6d11', border:'0.5px solid #3b6d11', color:'#f5f0e8'}}>
                                        Publish
                                    </button>
                                )}
                                <button onClick={() => deleteThread(t)}
                                    style={{...btnStyle, color:'#8b1a1a', borderColor:'#d4a8a0'}}>
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

const SettingsTab = () => {
    const [settings, setSettings] = useState({});
    const [loading,  setLoading]  = useState(true);
    const [saving,   setSaving]   = useState(false);
    const [edited,   setEdited]   = useState({});

    useEffect(() => {
        api.get('/admin/settings')
            .then(r => setSettings(r.data.settings || {}))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleChange = (key, value) => {
        setEdited(p => ({ ...p, [key]: value }));
    };

    const handleSave = async () => {
        if (Object.keys(edited).length === 0) return;
        setSaving(true);
        try {
            await api.put('/admin/settings', { settings: edited });
            toast.success('Settings saved');
            setEdited({});
            // Refresh
            const r = await api.get('/admin/settings');
            setSettings(r.data.settings || {});
        } catch { toast.error('Failed to save settings'); }
        finally { setSaving(false); }
    };

    const DISPLAY_KEYS = [
        // Site identity
        'site_name', 'site_tagline', 'homepage_hero_text',
        // Homepage hub cards
        'hub1_tag', 'hub1_title', 'hub1_desc', 'hub1_cta',
        'hub2_tag', 'hub2_title', 'hub2_desc', 'hub2_cta',
        'hub3_tag', 'hub3_title', 'hub3_desc', 'hub3_cta',
        // Store
        'store_tagline', 'free_shipping_threshold',
        // Social media
        'social_facebook', 'social_twitter', 'social_instagram', 'social_youtube',
        // Civic actions (used in Take Action page)
        'action_tracker_enabled',
        // Pre-launch banner (Store / Premium pages)
        'prelaunch_banner_enabled', 'prelaunch_banner_text',
        // Maintenance
        'maintenance_mode',
    ];

    if (loading) return <div style={{ padding: '2rem', fontFamily: 'sans-serif', color: '#8a8070' }}>Loading...</div>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080' }}>
                    {Object.keys(edited).length > 0 && (
                        <span style={{ color: '#8b6a3a' }}>{Object.keys(edited).length} unsaved change{Object.keys(edited).length > 1 ? 's' : ''}</span>
                    )}
                </div>
                <button onClick={handleSave} disabled={saving || Object.keys(edited).length === 0}
                    style={{ background: Object.keys(edited).length > 0 ? '#8b1a1a' : '#c8beaa', color: '#f5f0e8', border: 'none', padding: '8px 20px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: Object.keys(edited).length > 0 ? 'pointer' : 'not-allowed', borderRadius: '3px' }}>
                    {saving ? 'Saving...' : 'Save changes'}
                </button>
            </div>

            {DISPLAY_KEYS.map(key => {
                if (!settings[key]) return null;
                const s     = settings[key];
                const value = edited[key] !== undefined ? edited[key] : s.value;
                const isBool = value === 'true' || value === 'false';

                return (
                    <div key={key} style={{ marginBottom: '1rem', padding: '12px', background: edited[key] !== undefined ? '#fff8f0' : '#f5f0e8', border: `0.5px solid ${edited[key] !== undefined ? '#8b6a3a' : '#c8beaa'}`, borderRadius: '3px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontFamily: 'sans-serif', color: '#6a7080', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '4px' }}>
                            {({
                                site_name:              'Site name',
                                site_tagline:           'Site tagline',
                                homepage_hero_text:     'Homepage hero text',
                                hub1_tag:               'Card 1 — tag label',
                                hub1_title:             'Card 1 — title',
                                hub1_desc:              'Card 1 — description',
                                hub1_cta:               'Card 1 — button text',
                                hub2_tag:               'Card 2 — tag label',
                                hub2_title:             'Card 2 — title',
                                hub2_desc:              'Card 2 — description',
                                hub2_cta:               'Card 2 — button text',
                                hub3_tag:               'Card 3 — tag label',
                                hub3_title:             'Card 3 — title',
                                hub3_desc:              'Card 3 — description',
                                hub3_cta:               'Card 3 — button text',
                                store_tagline:          'Store tagline',
                                free_shipping_threshold:'Free shipping threshold ($)',
                                social_facebook:        'Facebook URL',
                                social_twitter:         'Twitter/X URL',
                                social_instagram:       'Instagram URL',
                                social_youtube:         'YouTube URL',
                                action_tracker_enabled: 'Civic actions enabled',
                                prelaunch_banner_enabled: 'Pre-launch banner (Store/Premium)',
                                prelaunch_banner_text:   'Pre-launch banner text',
                                maintenance_mode:       'Maintenance mode'
                            })[key] || key.replace(/_/g, ' ')}
                        </label>
                        {s.description && (
                            <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginBottom: '6px' }}>{s.description}</div>
                        )}
                        {isBool ? (
                            <select value={value} onChange={e => handleChange(key, e.target.value)}
                                style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', padding: '6px 10px', fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', borderRadius: '3px', outline: 'none' }}>
                                <option value="true">Enabled</option>
                                <option value="false">Disabled</option>
                            </select>
                        ) : (key.endsWith('_desc') || key === 'prelaunch_banner_text') ? (
                            <textarea value={value} onChange={e => handleChange(key, e.target.value)} rows={3}
                                style={{ width: '100%', background: '#f5f0e8', border: '0.5px solid #c8beaa', padding: '7px 10px', fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', borderRadius: '3px', outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
                        ) : (
                            <input type="text" value={value} onChange={e => handleChange(key, e.target.value)}
                                style={{ width: '100%', background: '#f5f0e8', border: '0.5px solid #c8beaa', padding: '7px 10px', fontSize: '13px', fontFamily: 'sans-serif', color: '#1e2a3a', borderRadius: '3px', outline: 'none', boxSizing: 'border-box' }} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// -- ACCOUNTING --
const AccountingTab = () => {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/admin/pnl')
            .then(r => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div style={{ padding: '2rem', fontFamily: 'sans-serif', color: '#8a8070' }}>Loading...</div>;
    if (!data) return <div style={{ padding: '2rem', fontFamily: 'sans-serif', color: '#8a8070' }}>No data available.</div>;

    const { products, totals } = data;

    return (
        <div>
            {/* Totals */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '1.5rem' }}>
                <StatCard label="Gross revenue"  value={fmt$(totals.gross_revenue)} />
                <StatCard label="Total costs"    value={fmt$(totals.total_costs)} color="#8b1a1a" />
                <StatCard label="Net revenue"    value={fmt$(totals.net_revenue)} color="#3b6d11" />
                <StatCard label="Units sold"     value={parseInt(totals.units_sold).toLocaleString()} />
            </div>

            {/* Per product */}
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e2a3a', marginBottom: '10px', fontFamily: "'EB Garamond', Georgia, serif" }}>
                Revenue by product
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: 'sans-serif' }}>
                <thead>
                    <tr style={{ background: '#ede8dc' }}>
                        {['Product', 'Units sold', 'Gross revenue', 'Costs', 'Net revenue', 'First sale', 'Last sale'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#4a5060', fontWeight: 600, borderBottom: '0.5px solid #c8beaa', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {products.map((p, i) => (
                        <tr key={p.slug} style={{ background: i % 2 === 0 ? '#f5f0e8' : '#ede8dc' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1e2a3a' }}>{p.name}</td>
                            <td style={{ padding: '8px 12px', color: '#4a5060', textAlign: 'center' }}>{p.units_sold || 0}</td>
                            <td style={{ padding: '8px 12px', color: '#1e2a3a' }}>{fmt$(p.gross_revenue_cents)}</td>
                            <td style={{ padding: '8px 12px', color: '#8b1a1a' }}>{fmt$(p.total_costs_cents)}</td>
                            <td style={{ padding: '8px 12px', color: '#3b6d11', fontWeight: 600 }}>{fmt$(p.net_revenue_cents)}</td>
                            <td style={{ padding: '8px 12px', color: '#8a8070', whiteSpace: 'nowrap' }}>{fmtDate(p.first_sale_at)}</td>
                            <td style={{ padding: '8px 12px', color: '#8a8070', whiteSpace: 'nowrap' }}>{fmtDate(p.last_sale_at)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ============================================================
// AdminPage -- main shell with tab navigation
// ============================================================
const AdminPage = () => {
    const { user, isAdmin, loading } = useAuth();
    const navigate                   = useNavigate();
    const [activeTab,    setActiveTab]    = useState('overview');
    const [permissions,  setPermissions]  = useState([]);
    const [permsLoading, setPermsLoading] = useState(true);

    useEffect(() => {
        if (!loading && (!user || !isAdmin)) {
            navigate('/', { replace: true });
        }
    }, [user, isAdmin, loading, navigate]);

    useEffect(() => {
        if (!user || !isAdmin) return;
        api.get('/admin/my-permissions')
            .then(r => setPermissions(r.data.permissions || []))
            .catch(() => setPermissions([]))
            .finally(() => setPermsLoading(false));
    }, [user, isAdmin]);

    const isSuperAdmin = user?.role === 'superadmin';
    const can = (perm) => isSuperAdmin || permissions.includes(perm);

    if (loading || permsLoading) return null;
    if (!user || !isAdmin) return null;

    const ALL_TABS = [
        { id: 'overview',   label: 'Overview',   always: true },
        { id: 'orders',     label: 'Orders',     perm: 'orders' },
        { id: 'articles',   label: 'Articles',   perm: 'articles' },
        { id: 'categories', label: 'Categories', perm: 'articles' },
        { id: 'products',   label: 'Products',   perm: 'products' },
        { id: 'users',      label: 'Users',      perm: 'users' },
        { id: 'accounting', label: 'Accounting', perm: 'accounting' },
        { id: 'chapters',   label: 'Chapters',   perm: 'chapters' },
        { id: 'forum',      label: 'Forum',      perm: 'articles' },
        { id: 'actions',    label: 'Actions',    perm: 'articles' },
        { id: 'settings',   label: 'Settings',   perm: 'settings' }
    ];

    const tabs = ALL_TABS.filter(t => t.always || can(t.perm));

    return (
        <>
            <Helmet><title>Admin Dashboard — By Our Consent</title></Helmet>

            {/* Admin nav */}
            <div style={{ background: '#1e2a3a', height: '56px', display: 'flex', alignItems: 'center', padding: '0 1.5rem', justifyContent: 'space-between' }}>
                <Link to="/" style={{ color: '#e8dfc8', fontSize: '17px', fontWeight: 600, fontFamily: "'EB Garamond', Georgia, serif", textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg width="18" height="22" viewBox="0 0 28 32" fill="none"><path d="M14 1L2 6v10c0 8.5 5.2 15.4 12 17.4C20.8 31.4 26 24.5 26 16V6L14 1z" fill="#8b1a1a" stroke="#c8beaa" strokeWidth="0.8"/></svg>
                    By Our Consent
                </Link>
                <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8b6a3a', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    Admin Dashboard
                </div>
                <Link to="/" style={{ color: '#b8ad98', fontSize: '12px', fontFamily: 'sans-serif', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    ← Back to site
                </Link>
            </div>

            {/* Tab bar */}
            <div style={{ background: '#ede8dc', borderBottom: '0.5px solid #c8beaa', padding: '0 1.5rem', display: 'flex', gap: '0' }}>
                {tabs.map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        style={{
                            background:    'transparent',
                            border:        'none',
                            borderBottom:  activeTab === tab.id ? '2px solid #8b1a1a' : '2px solid transparent',
                            padding:       '12px 18px',
                            fontSize:      '13px',
                            fontFamily:    'sans-serif',
                            color:         activeTab === tab.id ? '#1e2a3a' : '#6a7080',
                            cursor:        'pointer',
                            fontWeight:    activeTab === tab.id ? 600 : 400,
                            whiteSpace:    'nowrap'
                        }}>
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '1.5rem' }}>
                {activeTab === 'overview'   && <OverviewTab />}
                {activeTab === 'orders'     && <OrdersTab />}
                {activeTab === 'articles'   && <ArticlesTab />}
                {activeTab === 'categories' && <CategoriesTab />}
                {activeTab === 'products'   && <ProductsTab />}
                {activeTab === 'users'      && <UsersTab isSuperAdmin={isSuperAdmin} />}
                {activeTab === 'accounting' && <AccountingTab />}
                {activeTab === 'chapters'   && <ChaptersTab />}
                {activeTab === 'actions'    && <ActionsTab />}
                {activeTab === 'forum'      && <ForumTab />}
                {activeTab === 'settings'   && <SettingsTab />}
            </div>
        </>
    );
};

export default AdminPage;
